import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const I18nContext = createContext(null);

// --------------------------------------------------------------------------
// DICTIONARY — Italian (default, accessibility/matching/budget positioning)
// English (premium concierge, tailor-made yacht holidays positioning)
// --------------------------------------------------------------------------
const dict = {
  it: {
    nav: { howItWorks: "Come funziona", search: "Trova la tua barca", offers: "Offerte", contact: "Contatti", help: "Prima di salpare", language: "Lingua" },
    home: {
      eyebrow: "Il marketplace intelligente delle vacanze in barca",
      h1_part1: "Trova la vacanza in barca",
      h1_part2: "più adatta a te",
      h1_part3: ", anche quando non sai da dove iniziare.",
      subtitle: "Seamatch confronta disponibilità, budget, mete e tipologie di barca per proporti i migliori pacchetti disponibili e le alternative più intelligenti.",
      tagline: "Trova. Prenota. Naviga.",
      ctaPrimary: "Trova la tua vacanza",
      ctaSecondary: "Scopri come funziona",
      howEyebrow: "Come funziona",
      howTitle: "Quattro passi. Zero compromessi.",
      step1Title: "Raccontaci il viaggio",
      step1Desc: "Budget, date, gruppo e mete preferite. Bastano due minuti.",
      step2Title: "Confrontiamo l'intero mercato",
      step2Desc: "Il nostro team incrocia la tua richiesta con migliaia di disponibilità reali.",
      step3Title: "Ricevi proposte e alternative",
      step3Desc: "Ti mandiamo via email e WhatsApp i preventivi migliori e i suggerimenti smart se nessuna è perfetta.",
      step4Title: "Prenoti la soluzione giusta",
      step4Desc: "Scegli il pacchetto, blocchi la barca e parti.",
      whyEyebrow: "Perché Seamatch è diverso",
      whyTitle: "Non solo annunci. Un servizio che ragiona come un consulente.",
      whyP1: "I marketplace classici ti mostrano solo offerte filtrate alla lettera. Noi capiamo quando vale la pena spostare la rotta — e ti contattiamo con proposte già selezionate.",
      smartEyebrow: "Esempi di consigli intelligenti",
      smartTitle: "L'alternativa giusta, prima ancora che tu la chieda.",
      tip1Title: "Meglio barca a vela che catamarano",
      tip1Desc: "Con il tuo budget, una barca a vela ti offre un'esperienza simile a un costo decisamente più accessibile.",
      tip2Title: "Meglio Eolie che Sardegna",
      tip2Desc: "Le Isole Eolie offrono pacchetti compatibili con il tuo budget e maggiore disponibilità nelle settimane scelte.",
      tip3Title: "Cambiando settimana risparmi fino al 25%",
      tip3Desc: "Spostando le date di sette giorni, l'algoritmo trova soluzioni decisamente più convenienti.",
      // Hero stats strip
      strip1: "Solo Mediterraneo",
      strip2: "Risposta entro 24h",
      strip3: "Preventivi su misura",
      // Why-stats
      stat1Value: "+38%",
      stat1Label: "conversione richieste → preventivi",
      stat2Value: "92%",
      stat2Label: "preventivi pertinenti al primo invio",
      stat3Value: "−25%",
      stat3Label: "tempo medio di ricerca cliente",
      stat4Value: "10x",
      stat4Label: "alternative considerate per richiesta",
      // Smart tips chip labels
      chipPartners: "Budget",
      chipRoutes: "Meta",
      chipService: "Date",
    },
    search: {
      progress: "Passo", of: "di", next: "Avanti", back: "Indietro",
      submit: "Invia richiesta",
      s1Title: "Qual è il budget massimo complessivo per la vacanza?",
      s1Help: "Indica il budget totale per il gruppo, esclusi extra come voli, cambusa o transfer.",
      s2Title: "Quante persone parteciperanno?",
      s2Sub: "Che tipo di gruppo siete?",
      s3Title: "Vuoi prenotare una cabina o un'intera barca?",
      s4Title: "Che tipo di barca preferisci?",
      s5Title: "Dove vorresti andare?",
      s5Sub: "Inserisci fino a 3 mete in ordine di preferenza.",
      s6Title: "Quando vorresti partire?",
      s7Title: "Hai bisogno dello skipper?",
      s7Help: "Se non hai patente nautica o esperienza, lo skipper può essere necessario.",
      s8Title: "Che tipo di esperienza cerchi?",
      s9Title: "Dove possiamo mandarti i preventivi migliori?",
      s9Help: "Ti contatteremo via email e WhatsApp con le proposte più adatte alle tue esigenze.",
      privacy: "Acconsento al trattamento dei dati personali",
      marketing: "Voglio ricevere consigli di viaggio (facoltativo)",
      countryCode: "Prefisso",
      phone: "Numero di telefono",
      countryCodeHelp: "Seleziona il prefisso del tuo Paese",
      budgetCalloutEyebrow: "Come indicare il budget",
      budgetCalloutBody: "Indica il budget complessivo per l'intero gruppo. Se parti da solo, indica il tuo budget personale; se siete in 2, 4 o 8 persone, indica il budget totale disponibile per tutti i partecipanti.",
    },
    confirm: {
      title: "Richiesta ricevuta",
      sub: "Stiamo cercando le migliori soluzioni disponibili per te. Riceverai i preventivi più adatti direttamente ai recapiti che ci hai indicato.",
      ref: "Riferimento",
      step1: "Analizziamo la tua richiesta",
      step1Desc: "Il nostro team confronta budget, date e mete con la disponibilità reale delle società charter.",
      step2: "Selezioniamo le proposte migliori",
      step2Desc: "Ti mandiamo solo offerte già verificate, comprese eventuali alternative intelligenti.",
      step3: "Ti contattiamo entro 24h",
      step3Desc: "Email e WhatsApp con i preventivi più compatibili. Tu scegli, noi blocchiamo la barca.",
      home: "Torna alla homepage",
    },
    common: {
      perPerson: "a persona", total: "totale", nights: "notti", beds: "posti letto",
      withSkipper: "Skipper incluso", noSkipper: "Senza skipper",
      compat: "compatibilità", lastMinute: "Last minute", from: "Da", to: "a",
      close: "Chiudi", loading: "Caricamento…",
    },
    contact: {
      eyebrow: "Parla con noi",
      title: "Hai una domanda? Siamo qui per aiutarti.",
      sub: "Scrivici, chiamaci o usa il modulo qui sotto. Il nostro team ti risponde di solito in poche ore.",
      formName: "Nome e cognome", formEmail: "Email", formMsg: "Messaggio",
      submit: "Invia messaggio", success: "Messaggio inviato. Ti risponderemo a breve.",
      error: "Errore nell'invio. Riprova tra qualche minuto.",
    },
    help: {
      eyebrow: "Prima di salpare",
      title: "Tutto quello che ti serve sapere prima della tua vacanza in barca",
      sub: "Guide, checklist e consigli pratici per partire informato e tranquillo.",
      backToList: "Torna all'indice",
    },
    privacy: {
      title: "Informativa sulla privacy",
      updated: "Ultimo aggiornamento",
    },
    proposal: {
      title: "Le tue proposte",
      sub: "Abbiamo selezionato queste soluzioni per te. Scegli quella che ti convince di più, ci occupiamo noi del resto.",
      iAmInterested: "Mi interessa questa proposta",
      alreadyInterested: "Hai già confermato il tuo interesse",
      details: "Dettagli imbarcazione",
      destination: "Destinazione",
      port: "Porto di partenza",
      dates: "Date disponibili",
      includes: "Cosa include",
      excludes: "Cosa NON include",
      price: "Prezzo",
      notFound: "Portale non disponibile o link scaduto",
      confirmedTitle: "Interesse confermato",
      confirmedSub: "Abbiamo ricevuto il tuo interesse. Il nostro team ti contatterà entro 24 ore per finalizzare la prenotazione.",
    },
    detail: {
      gallery: "Galleria", itinerary: "Itinerario indicativo",
      included: "Cosa è incluso", excluded: "Cosa non è incluso",
      why: "Perché te la consigliamo",
      cta: "Richiedi disponibilità", cta2: "Parla con un consulente",
      back: "Torna indietro",
    },
    partner: {
      heroEyebrow: "Per società charter & broker",
      heroTitle: "Riempire le settimane vuote non è più un problema",
      heroSub: "Seamatch porta lead già qualificati. Tu decidi quali settimane vendere e a quale prezzo.",
      bullet1: "Lead pre-filtrati per budget, destinazione e tipologia",
      bullet2: "Suggerimenti automatici per spingere le settimane scariche",
      bullet3: "Dashboard pulita: flotta, calendario, richieste, prenotazioni",
      cta: "Diventa partner",
      already: "Sei già partner?",
      login: "Accedi alla dashboard",
      loginTitle: "Accedi alla dashboard charter",
      loginSub: "Inserisci l'email registrata. Per la demo usa demo@charter.com.",
      registerTitle: "Diventa partner Seamatch",
      registerSub: "Bastano pochi dati per pubblicare la tua flotta sulla piattaforma.",
      companyName: "Nome società",
      contactName: "Nome referente",
      email: "Email",
      phone: "Telefono",
      website: "Sito web",
      areas: "Aree geografiche operative",
      areasPh: "Es. Sardegna, Croazia",
      numBoats: "Numero barche gestite",
      submit: "Crea account partner",
    },
    dashboard: {
      welcome: "Benvenuto", overview: "Panoramica", fleet: "Gestione flotta",
      requests: "Richieste clienti", lastMin: "Offerte last minute",
      kpiWeeks: "Settimane disponibili", kpiRequests: "Richieste ricevute",
      kpiQuotes: "Preventivi inviati", kpiBookings: "Prenotazioni generate",
      kpiPotential: "Valore potenziale", addBoat: "Aggiungi barca",
      createLM: "Crea offerta last minute", noLeads: "Nessuna richiesta compatibile al momento.",
    },
    footer: {
      tagline: "Trova. Prenota. Naviga.",
      product: "Prodotto", howItWorks: "Come funziona", findBoat: "Trova la tua barca",
      help: "Prima di salpare", company: "Azienda", contact: "Contatti",
      privacy: "Privacy policy", partner: "Per i charter",
      copy: "© Seamatch — Tutti i diritti riservati",
    },
  },

  en: {
    nav: { howItWorks: "How it works", search: "Plan your trip", offers: "Yachts", contact: "Concierge", help: "Sailing guide", language: "Language" },
    home: {
      eyebrow: "Tailor-made Mediterranean sailing",
      h1_part1: "Tailor-made yacht holidays",
      h1_part2: "in the Mediterranean",
      h1_part3: ". Curated by experts.",
      subtitle: "Seamatch designs personal Mediterranean sailing holidays, hand-built around your group, your itinerary and your taste. Curated by experts, delivered through a small network of trusted charter partners.",
      tagline: "Tailor-made yacht holidays in the Mediterranean. Curated by experts.",
      ctaPrimary: "Plan my yacht holiday",
      ctaSecondary: "How we work",
      howEyebrow: "The Seamatch experience",
      howTitle: "A curated holiday, in four quiet steps.",
      step1Title: "Share your story",
      step1Desc: "A short conversation about your group, your itinerary and the experience you have in mind. No forms to obsess over.",
      step2Title: "We curate the right yachts",
      step2Desc: "Our specialists select from a vetted network of charter partners — yachts we have personally sailed, crews we trust.",
      step3Title: "Receive your bespoke proposal",
      step3Desc: "A single, carefully assembled itinerary delivered by your concierge. No noise, no listings, just the right option.",
      step4Title: "Sail with full support",
      step4Desc: "From boarding day to disembarkation, your concierge stays one message away. You travel, we take care of the rest.",
      whyEyebrow: "Why travellers choose Seamatch",
      whyTitle: "A private yacht concierge, not a booking portal.",
      whyP1: "Marketplaces show you whatever matches your filters. We listen to what you actually want — the itinerary, the crew style, the harbours you'd love to wake up in — and we design a holiday that fits. Quietly, precisely, on your terms.",
      smartEyebrow: "How we curate",
      smartTitle: "The right yacht. The right week. The right people on board.",
      tip1Title: "Vetted yachts only",
      tip1Desc: "We work with a closed circle of charter partners across the Mediterranean. Every yacht is inspected, every crew personally known.",
      tip2Title: "Insider routes",
      tip2Desc: "From the Aeolian Islands and Sardinia to the Greek Cyclades and the Dalmatian coast — our team knows the harbours, anchorages and tables worth pausing for.",
      tip3Title: "Effortless planning",
      tip3Desc: "One concierge, one proposal, one point of contact. We coordinate the charter, the skipper, the provisioning and the small details that make a holiday memorable.",
      // Hero stats strip
      strip1: "Mediterranean only",
      strip2: "24-hour personal reply",
      strip3: "Vetted charter partners",
      // Why-stats (replaced with premium-oriented copy)
      stat1Value: "100%",
      stat1Label: "Itineraries tailor-made",
      stat2Value: "24h",
      stat2Label: "Concierge response time",
      stat3Value: "1:1",
      stat3Label: "Dedicated specialist",
      stat4Value: "Med.",
      stat4Label: "Only the Mediterranean",
      // Smart tips chip labels
      chipPartners: "Partners",
      chipRoutes: "Routes",
      chipService: "Service",
    },
    search: {
      progress: "Step", of: "of", next: "Next", back: "Back",
      submit: "Send to my concierge",
      s1Title: "What budget would you like us to work within?",
      s1Help: "A guideline figure for the whole party — flights and provisioning excluded. Your concierge will tailor the proposal accordingly.",
      s2Title: "Who will be on board?",
      s2Sub: "Tell us about your party.",
      s3Title: "Whole yacht or cabin charter?",
      s4Title: "Any preference on the type of yacht?",
      s5Title: "Where would you like to sail?",
      s5Sub: "Choose up to three destinations in order of preference.",
      s6Title: "When would you like to set sail?",
      s7Title: "Would you like a skipper on board?",
      s7Help: "Recommended unless you hold a valid sailing licence and recent open-water experience.",
      s8Title: "What kind of experience would you like to live?",
      s9Title: "Where shall we send your bespoke proposal?",
      s9Help: "Your concierge will be in touch by email and WhatsApp with a carefully assembled itinerary.",
      privacy: "I agree to the processing of my personal data",
      marketing: "I'd like to receive curated travel inspiration (optional)",
      countryCode: "Country code",
      phone: "Phone number",
      countryCodeHelp: "Select your country code",
      // Budget step info callout
      budgetCalloutEyebrow: "How to set your budget",
      budgetCalloutBody: "Please indicate a total guideline figure for the whole party. Your concierge will use it to design the most fitting itinerary — and will quietly suggest adjustments where they make sense.",
    },
    confirm: {
      title: "Your request is in good hands",
      sub: "Your dedicated concierge is already reviewing the details and will return with a hand-built proposal shortly.",
      ref: "Reference",
      step1: "Personal review",
      step1Desc: "A specialist studies your brief — destination, dates, crew style, the experience you have in mind.",
      step2: "Curated selection",
      step2Desc: "We approach a short list of trusted charter partners and assemble a single, fitting proposal for you.",
      step3: "Concierge follow-up",
      step3Desc: "Within 24 hours your concierge will be in touch by email and WhatsApp, ready to refine every detail with you.",
      home: "Return to Seamatch",
    },
    common: {
      perPerson: "per guest", total: "total", nights: "nights", beds: "berths",
      withSkipper: "Skipper on board", noSkipper: "Bareboat",
      compat: "match", lastMinute: "Late availability", from: "From", to: "to",
      close: "Close", loading: "Loading…",
    },
    contact: {
      eyebrow: "Speak with a concierge",
      title: "Your Seamatch concierge is one message away.",
      sub: "Send us a brief and we'll come back with thoughtful answers and ideas — typically within a few hours.",
      formName: "Full name", formEmail: "Email", formMsg: "Message",
      submit: "Send to concierge", success: "Thank you. Your concierge will be in touch shortly.",
      error: "Something went wrong. Please try again in a moment.",
    },
    help: {
      eyebrow: "Sailing guide",
      title: "Notes from our concierge team — before you set sail",
      sub: "A curated collection of essays, checklists and quiet advice from the specialists who design every Seamatch holiday.",
      backToList: "Back to the guide",
    },
    privacy: {
      title: "Privacy notice",
      updated: "Last updated",
    },
    proposal: {
      title: "Your curated proposal",
      sub: "Your concierge has hand-built the option below. Should it feel right, simply confirm — we will take care of every remaining detail with the charter partner.",
      iAmInterested: "Confirm interest",
      alreadyInterested: "Interest already confirmed",
      details: "About the yacht",
      destination: "Destination",
      port: "Departure port",
      dates: "Sailing dates",
      includes: "Included on board",
      excludes: "Not included",
      price: "Charter fee",
      notFound: "This portal is no longer available",
      confirmedTitle: "Interest confirmed",
      confirmedSub: "Your concierge will be in touch within 24 hours to finalise every detail with the charter partner.",
    },
    detail: {
      gallery: "Gallery", itinerary: "Suggested itinerary",
      included: "Included on board", excluded: "Not included",
      why: "Why our concierge selected it",
      cta: "Request availability", cta2: "Speak with a concierge",
      back: "Go back",
    },
    partner: {
      heroEyebrow: "For charter companies & brokers",
      heroTitle: "Curated demand, delivered quietly to your fleet",
      heroSub: "Seamatch sends pre-qualified, ready-to-book travellers to a small circle of trusted charter partners.",
      bullet1: "Hand-screened guests with a clear brief",
      bullet2: "Concierge support throughout the booking journey",
      bullet3: "Calm, low-volume, high-conversion channel",
      cta: "Apply to join", already: "Already a partner?",
      login: "Partner login", loginTitle: "Partner login",
      loginSub: "Use your registered email. For the preview try demo@charter.com.",
      registerTitle: "Apply to become a Seamatch partner",
      registerSub: "Tell us about your fleet. We onboard a small number of partners every season.",
      companyName: "Company", contactName: "Primary contact",
      email: "Email", phone: "Phone", website: "Website",
      areas: "Operating regions", areasPh: "e.g. Sardinia, Croatia",
      numBoats: "Fleet size", submit: "Send application",
    },
    dashboard: {
      welcome: "Welcome", overview: "Overview", fleet: "Fleet",
      requests: "Guest briefs", lastMin: "Late availability",
      kpiWeeks: "Open weeks", kpiRequests: "Guest briefs",
      kpiQuotes: "Proposals sent", kpiBookings: "Confirmed bookings",
      kpiPotential: "Pipeline value", addBoat: "Add yacht",
      createLM: "Open a late-availability slot", noLeads: "No matching guest briefs at the moment.",
    },
    footer: {
      tagline: "Tailor-made yacht holidays in the Mediterranean. Curated by experts.",
      product: "Seamatch", howItWorks: "How it works", findBoat: "Plan your trip",
      help: "Sailing guide", company: "House", contact: "Concierge",
      privacy: "Privacy notice", partner: "Charter partners",
      copy: "© Seamatch — All rights reserved",
    },
  },
};

// --------------------------------------------------------------------------
// Country list for phone code dropdown (used in Search step contacts).
// Ordered by relevance for Seamatch traffic (IT first, then EU + EN-speakers).
// --------------------------------------------------------------------------
export const PHONE_COUNTRIES = [
  { code: "+39",  iso: "IT", flag: "🇮🇹", labels: { it: "Italia",            en: "Italy" } },
  { code: "+1",   iso: "US", flag: "🇺🇸", labels: { it: "USA / Canada",      en: "USA / Canada" } },
  { code: "+44",  iso: "GB", flag: "🇬🇧", labels: { it: "Regno Unito",       en: "United Kingdom" } },
  { code: "+49",  iso: "DE", flag: "🇩🇪", labels: { it: "Germania",          en: "Germany" } },
  { code: "+33",  iso: "FR", flag: "🇫🇷", labels: { it: "Francia",           en: "France" } },
  { code: "+34",  iso: "ES", flag: "🇪🇸", labels: { it: "Spagna",            en: "Spain" } },
  { code: "+31",  iso: "NL", flag: "🇳🇱", labels: { it: "Paesi Bassi",       en: "Netherlands" } },
  { code: "+41",  iso: "CH", flag: "🇨🇭", labels: { it: "Svizzera",          en: "Switzerland" } },
  { code: "+43",  iso: "AT", flag: "🇦🇹", labels: { it: "Austria",           en: "Austria" } },
  { code: "+32",  iso: "BE", flag: "🇧🇪", labels: { it: "Belgio",            en: "Belgium" } },
  { code: "+46",  iso: "SE", flag: "🇸🇪", labels: { it: "Svezia",            en: "Sweden" } },
  { code: "+47",  iso: "NO", flag: "🇳🇴", labels: { it: "Norvegia",          en: "Norway" } },
  { code: "+45",  iso: "DK", flag: "🇩🇰", labels: { it: "Danimarca",         en: "Denmark" } },
  { code: "+353", iso: "IE", flag: "🇮🇪", labels: { it: "Irlanda",           en: "Ireland" } },
  { code: "+351", iso: "PT", flag: "🇵🇹", labels: { it: "Portogallo",        en: "Portugal" } },
  { code: "+30",  iso: "GR", flag: "🇬🇷", labels: { it: "Grecia",            en: "Greece" } },
  { code: "+61",  iso: "AU", flag: "🇦🇺", labels: { it: "Australia",         en: "Australia" } },
  { code: "+",    iso: "ZZ", flag: "🌍",  labels: { it: "Altro Paese…",      en: "Other country…" } },
];

// --------------------------------------------------------------------------
// Auto-detect: localStorage > URL prefix > navigator > geo IP > default IT
// --------------------------------------------------------------------------
const EN_COUNTRIES = new Set(["US", "GB", "DE", "FR", "ES", "NL", "CH", "AT", "BE", "SE", "NO", "DK", "IE", "PT", "GR", "AU", "CA", "NZ", "FI", "IS", "PL", "CZ", "HU"]);

function detectInitialLang() {
  try {
    // 1. URL prefix has the highest priority
    const path = (typeof window !== "undefined" && window.location?.pathname) || "";
    if (path.startsWith("/en")) return "en";
    if (path.startsWith("/it")) return "it";
    // 2. saved preference
    const saved = typeof localStorage !== "undefined" ? localStorage.getItem("sm_lang") : null;
    if (saved === "it" || saved === "en") return saved;
    // 3. browser language
    const nav = typeof navigator !== "undefined" ? (navigator.language || navigator.userLanguage || "") : "";
    if (nav.toLowerCase().startsWith("it")) return "it";
    if (nav) return "en";
  } catch { /* noop */ }
  return "it";
}

async function geoDetect() {
  try {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const r = await fetch(`${base}/api/geo/country`);
    if (!r.ok) return null;
    const data = await r.json();
    if (!data?.country) return null;
    if (data.country === "IT") return "it";
    if (EN_COUNTRIES.has(data.country)) return "en";
    return null;
  } catch { return null; }
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectInitialLang);
  const [userOverride, setUserOverride] = useState(() => {
    try {
      return typeof localStorage !== "undefined" && !!localStorage.getItem("sm_lang");
    } catch { return false; }
  });

  // Geo detection only if no saved preference and no URL prefix
  useEffect(() => {
    if (userOverride) return;
    const path = window.location?.pathname || "";
    if (path.startsWith("/en") || path.startsWith("/it")) return;
    const saved = localStorage.getItem("sm_lang");
    if (saved) return;
    geoDetect().then((detected) => {
      if (detected && detected !== lang) setLang(detected);
    });
  }, [userOverride, lang]);

  // Sync <html lang="…"> for SEO / accessibility
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(() => {
    const change = (l) => {
      setLang(l);
      setUserOverride(true);
      try { localStorage.setItem("sm_lang", l); } catch { /* noop */ }
    };
    const t = (path) => {
      const keys = path.split(".");
      let cur = dict[lang];
      for (const k of keys) {
        if (cur && typeof cur === "object" && k in cur) cur = cur[k];
        else return path;
      }
      return cur;
    };
    // Localized path helper — prepends /it or /en if current URL is scoped
    const localizePath = (path) => {
      if (!path || !path.startsWith("/")) return path;
      // If current URL is scoped to a lang, keep it
      const cur = window.location?.pathname || "";
      const scoped = cur.startsWith("/en/") || cur === "/en" ? "/en" : (cur.startsWith("/it/") || cur === "/it" ? "/it" : "");
      if (!scoped) return path;
      if (path.startsWith(scoped + "/") || path === scoped) return path;
      // Don't prefix admin/partners (separate areas)
      if (path.startsWith("/admin") || path.startsWith("/partners")) return path;
      return `${scoped}${path}`;
    };
    return { lang, setLang: change, t, localizePath };
  }, [lang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() { return useContext(I18nContext); }

// Component to force a specific language while a sub-tree is mounted (used by /it/* and /en/* routes)
export function LangScope({ lang, children }) {
  const ctx = useI18n();
  useEffect(() => {
    if (ctx && lang && ctx.lang !== lang) {
      // Direct (don't persist as override — URL is the source of truth here)
      // Use the public setLang because it persists; URL prefix wins anyway on next detect.
      ctx.setLang(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  return children;
}
